import React, { useEffect, useMemo } from 'react';
import { Select, Checkbox } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, setSelectedMetros, setIsAllSelected, setPageNumberForDocument } from '../store';
import './fall-back.scss';
import { Tooltip } from 'antd';

const { Option } = Select;

interface MetroFilterProps {
  metros: any[];
  onApply: (selectedMetros: string[]) => void;
}

export const MetroFilter: React.FC<MetroFilterProps> = ({ metros, onApply }) => {
  const dispatch = useDispatch();
  const { selectedMetros, isAllSelected } = useSelector((state: RootState) => state.document);

  // Sort metros alphabetically using useMemo for optimization
  const sortedMetros = useMemo(() => {
    return [...metros].sort((a, b) => a.name.localeCompare(b.name));
  }, [metros]);

  useEffect(() => {
    if (isAllSelected && sortedMetros.length > 0 && selectedMetros.length === sortedMetros.length) {
      onApply(selectedMetros);
    }
  }, [sortedMetros, isAllSelected, selectedMetros, onApply]);

  const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const truncate = (s: string, n = 11) =>
    s && s.length > n ? s.substring(0, n) + '...' : s;

  const handleChange = (values: string[]) => {
    const allOptionIndex = values.indexOf('all');

    if (allOptionIndex !== -1) {
      if (isAllSelected) {
        dispatch(setSelectedMetros([]));
        onApply([]);
        dispatch(setIsAllSelected(false));
      } else {
        const allMetroNames = sortedMetros.map(metro => metro.name);
        dispatch(setSelectedMetros(allMetroNames));
        onApply(allMetroNames);
        dispatch(setIsAllSelected(true));
      }
    } else {
      dispatch(setSelectedMetros(values));
      dispatch(setPageNumberForDocument("1"));
      onApply(values);
      dispatch(setIsAllSelected(values.length === sortedMetros.length));
    }
  };

  return (
    <div className="metro-filter">
      <Select
        mode="multiple"
        placeholder="All"
        style={{ width: '100%', minWidth: '120px', cursor: 'pointer' }}
        value={selectedMetros}
        onChange={handleChange}
        showSearch={true}
        maxTagCount={0}
        maxTagPlaceholder={() => `${selectedMetros.length}`}
        optionLabelProp="label"
        className="custom-select-no-ticks"
        menuItemSelectedIcon={null}
      >
        <Option key="all" value="all" label={'Select All'}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox checked={isAllSelected} style={{ marginRight: 8 }} />
            <span style={{ fontWeight: 'bold' }}>{'Select All'}</span>
          </div>
        </Option>

        {sortedMetros.map((metro) => {
          const fullName = capitalizeFirstLetter(metro.name);
          const displayName = truncate(fullName, 11);
          return (
            <Option
              key={metro.name}
              value={metro.name}
              label={displayName}
            >
              <Tooltip title={fullName} placement="right"
                styles={{
                  body: {
                    padding: '2px 6px',
                    minHeight: 'auto',
                  },
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox checked={selectedMetros.includes(metro.name)} style={{ marginRight: 8 }} />
                  <span >{displayName}</span>
                </div>
              </Tooltip>
            </Option>
          );
        })}
      </Select>
    </div>
  );
};


